// src/pages/Contact.js (or wherever your ContactUs lives)
import React, { useEffect, useMemo, useState } from "react";
import { NeuralNoise } from "../components/ui/neural-noise";
import { useDynamicCSS } from "../hooks/DynamicCSSLoader";
import { useScrollAnimation, useStaggeredAnimation } from "../hooks/ScrollAnimation";
import "../styles/ScrollAnimations.css";

const IconMail = () => (
  <svg viewBox="0 0 24 24" className="ctIconSvg" aria-hidden="true">
    <path
      d="M4 6h16v12H4V6zm1.5 1.5v.2l6.5 5 6.5-5v-.2H5.5zm13 9V9.2l-6.1 4.7a1 1 0 0 1-1.2 0L5.5 9.2V16.5h13z"
      fill="currentColor"
    />
  </svg>
);

const IconPhone = () => (
  <svg viewBox="0 0 24 24" className="ctIconSvg" aria-hidden="true">
    <path
      d="M6.6 10.8c1.5 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.8-.4 1.2-.2 1.3.5 2.7.8 4.2.8.7 0 1.2.5 1.2 1.2V20c0 .7-.5 1.2-1.2 1.2C10.2 21.2 2.8 13.8 2.8 4.2 2.8 3.5 3.3 3 4 3h3.2c.7 0 1.2.5 1.2 1.2 0 1.5.3 2.9.8 4.2.1.4 0 .9-.2 1.2l-2.4 2.2z"
      fill="currentColor"
    />
  </svg>
);

const IconPin = () => (
  <svg viewBox="0 0 24 24" className="ctIconSvg" aria-hidden="true">
    <path
      d="M12 2c3.9 0 7 3.1 7 7 0 5.2-7 13-7 13S5 14.2 5 9c0-3.9 3.1-7 7-7zm0 9.5A2.5 2.5 0 1 0 12 6.5a2.5 2.5 0 0 0 0 5z"
      fill="currentColor"
    />
  </svg>
);

const IconChat = () => (
  <svg viewBox="0 0 24 24" className="ctIconSvg" aria-hidden="true">
    <path
      d="M4 4h16v12H7.2L4 19.2V4zm2 2v9.2L6.8 14H18V6H6z"
      fill="currentColor"
    />
  </svg>
);

const ContactUs = () => {
  useDynamicCSS("contact");

  // ✅ detect current theme from <html data-theme="...">
  const [theme, setTheme] = useState(
    document.documentElement.getAttribute("data-theme") || "dark"
  );

  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => {
      setTheme(el.getAttribute("data-theme") || "dark");
    });
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  // Animations
  const headerAnimation = useScrollAnimation({ threshold: 0.25, delay: 0 });
  const introAnimation = useScrollAnimation({ threshold: 0.2, delay: 120 });
  const contactMethodsAnimation = useStaggeredAnimation(4, {
    threshold: 0.2,
    staggerDelay: 110,
    rootMargin: "0px 0px -60px 0px",
  });
  const formAnimation = useScrollAnimation({ threshold: 0.22, delay: 180 });
  const supportAnimation = useScrollAnimation({ threshold: 0.2, delay: 220 });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    inquiryType: "general",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMessage) setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await fetch(
        "https://x9bek381o0.execute-api.us-east-1.amazonaws.com/con/con",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (result["message"] === "Sent successfully!") {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          inquiryType: "general",
        });
        setTimeout(() => setSubmitStatus(""), 5000);
      } else {
        throw new Error(result.error || "Failed to submit form");
      }
    } catch (error) {
      setErrorMessage(error.message || "Failed to send message. Please try again.");
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = useMemo(
    () => [
      {
        icon: <IconMail />,
        title: "Email Support",
        info: "contact@craftingbrain.com",
        subtext: "Reply within 24 hours",
        description: "Course details, enrollment help, or technical support—send it here.",
        action: { label: "Send Email", href: "mailto:contact@craftingbrain.com" },
      },
      {
        icon: <IconPhone />,
        title: "Academic Helpline",
        info: "+91 9115076096",
        subtext: "Mon–Fri · 9:00 AM – 6:00 PM IST",
        description: "Talk to an academic counselor for guidance based on your goals.",
        action: { label: "Call Now", href: "tel:+919115076096" },
      },
      {
        icon: <IconPin />,
        title: "India Office",
        info: "Hyderabad, Telangana",
        subtext: "Visit by appointment",
        description: "Meet the team and see the learning environment in-person.",
        action: {
          label: "Open Maps",
          href: "https://www.google.com/maps/search/?api=1&query=Hyderabad%2C%20Telangana",
        },
      },
      {
        icon: <IconChat />,
        title: "Live Chat",
        info: "Instant Assistance",
        subtext: "24/7 available",
        description: "Quick answers for admissions, batches, fees, and onboarding.",
        action: {
          label: "WhatsApp",
          href:
            "https://api.whatsapp.com/send?phone=+919305009726&text=Hello%2C%20I%20want%20to%20know%20more%20about%20your%20services",
        },
      },
    ],
    []
  );

  const noiseColor = theme === "light" ? [0.15, 0.45, 1.0] : [1.0, 0.65, 0.12];

  return (
    <div className="ctXPage">
      {/* ✅ Neural noise (behind) */}
      <NeuralNoise className="ctNeural" color={noiseColor} opacity={theme === "light" ? 0.26 : 0.22} speed={0.00035} />

      {/* Background layer */}
      <div className="ctXBg" aria-hidden="true">
        <div className="ctXGlow g1" />
        <div className="ctXGlow g2" />
        <div className="ctXGrid" />
      </div>

      <div className="ctXWrap">
        {/* HERO */}
        <header
          ref={headerAnimation.elementRef}
          className={`ctXHero section-title-animate ${headerAnimation.animationClass}`}
        >
          <div className="ctXHeroTopPill">
            <span className="dot" />
            Crafting Brain · Contact
          </div>

          <h1 className="ctXTitle">Connect With Our Learning Community</h1>
          <p className="ctXSub">
            Questions about AI / Data Science program, internship roadmap, or enrollment?
            Our team will guide you clearly and fast.
          </p>

          <div className="ctXHeroStats" role="list">
            <div className="ctXStat" role="listitem">
              <div className="k">Response</div>
              <div className="v">{"< 24 hrs"}</div>
            </div>
            <div className="ctXStat" role="listitem">
              <div className="k">Support</div>
              <div className="v">Admissions + Tech</div>
            </div>
            <div className="ctXStat" role="listitem">
              <div className="k">Channel</div>
              <div className="v">Email · WhatsApp · Call</div>
            </div>
          </div>
        </header>

        {/* INTRO */}
        <section
          ref={introAnimation.elementRef}
          className={`ctXIntro educational-fade ${introAnimation.animationClass}`}
        >
          <div className="ctXIntroCard">
            <h2>Tell us your goal — we’ll map a path</h2>
            <p>
              Whether you’re starting from zero or already building projects, we’ll suggest the
              right batch, the right pace, and the right outcomes. No confusion, just clarity.
            </p>
            <div className="ctXIntroBadges">
              <span className="ctXBadge">GenAI + Projects</span>
              <span className="ctXBadge">Internship Roadmap</span>
              <span className="ctXBadge">Career Guidance</span>
            </div>
          </div>
        </section>

        {/* MAIN GRID */}
        <section className="ctXMain">
          {/* LEFT */}
          <div className="ctXLeft">
            <div className="ctXBlockTitle">
              <h2>Get in Touch</h2>
              <p>Choose the fastest option for your question.</p>
            </div>

            <div ref={contactMethodsAnimation.containerRef} className="ctXCards stagger-container">
              {contactMethods.map((m, idx) => (
                <article
                  key={idx}
                  className={`ctXCard info-card-animate ${contactMethodsAnimation.getItemAnimationClass(idx)}`}
                >
                  <div className="ctXCardIcon">{m.icon}</div>

                  <div className="ctXCardBody">
                    <h3 className="ctXCardTitle">{m.title}</h3>
                    <div className="ctXCardInfo">{m.info}</div>
                    <div className="ctXCardSub">{m.subtext}</div>
                    <p className="ctXCardDesc">{m.description}</p>

                    <a className="ctXCardCta" href={m.action.href} target="_blank" rel="noreferrer">
                      {m.action.label}
                      <span className="arr">→</span>
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div className="ctXRight">
            <div
              ref={formAnimation.elementRef}
              className={`ctXFormShell form-animate ${formAnimation.animationClass}`}
            >
              <div className="ctXFormHead">
                <h2>Start Your Learning Journey</h2>
                <p>Fill the form and our team will contact you within 24 hours with the next steps.</p>
              </div>

              <form onSubmit={handleSubmit} className="ctXForm" noValidate>
                <div className="ctXField">
                  <label className="ctXLabel">What brings you here today?</label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleInputChange}
                    className="ctXSelect"
                    required
                  >
                    <option value="general">General Information</option>
                    <option value="course">Course Details & Curriculum</option>
                    <option value="enrollment">Enrollment Process</option>
                    <option value="technical">Technical Support</option>
                    <option value="partnership">Business Partnership</option>
                    <option value="career">Career Guidance</option>
                  </select>
                </div>

                <div className="ctXGrid2">
                  <div className="ctXField">
                    <label className="ctXLabel" htmlFor="name">
                      Full Name <span>*</span>
                    </label>
                    <input
                      className="ctXInput"
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="ctXField">
                    <label className="ctXLabel" htmlFor="email">
                      Email <span>*</span>
                    </label>
                    <input
                      className="ctXInput"
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="ctXGrid2">
                  <div className="ctXField">
                    <label className="ctXLabel" htmlFor="phone">Phone</label>
                    <input
                      className="ctXInput"
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div className="ctXField">
                    <label className="ctXLabel" htmlFor="subject">
                      Subject <span>*</span>
                    </label>
                    <input
                      className="ctXInput"
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="Brief subject line"
                      required
                    />
                  </div>
                </div>

                <div className="ctXField">
                  <label className="ctXLabel" htmlFor="message">
                    Tell us more <span>*</span>
                  </label>
                  <textarea
                    className="ctXTextarea"
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    placeholder="Your background, goals, and what you want to achieve…"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className={`ctXSubmit ${isSubmitting ? "isLoading" : ""}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="ctXSpinner" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <span className="ctXSend">📩</span>
                      Send Message
                    </>
                  )}
                </button>

                {submitStatus === "success" && (
                  <div className="ctXAlert ok" role="status">
                    <span className="i">✅</span>
                    <div>
                      <strong>Message sent!</strong>
                      <div>We’ll contact you within 24 hours.</div>
                    </div>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="ctXAlert err" role="alert">
                    <span className="i">❌</span>
                    <div>
                      <strong>Message not sent</strong>
                      <div>{errorMessage}</div>
                    </div>
                  </div>
                )}

                <div className="ctXPrivacy">
                  By submitting, you agree to be contacted by Crafting Brain for this enquiry.
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* SUPPORT STRIP */}
        <section
          ref={supportAnimation.elementRef}
          className={`ctXSupport educational-fade ${supportAnimation.animationClass}`}
        >
          <div className="ctXSupportInner">
            <div className="ctXSupportText">
              <h2>Need Immediate Assistance?</h2>
              <p>
                WhatsApp is fastest. For formal communication, use email. For quick counselling,
                schedule a call.
              </p>
            </div>

            <div className="ctXSupportBtns">
              <button
                className="ctXBtn primary"
                onClick={() =>
                  window.open(
                    "https://api.whatsapp.com/send?phone=+919305009726&text=Hello%2C%20I%20want%20to%20know%20more%20about%20your%20services",
                    "_blank"
                  )
                }
              >
                💬 WhatsApp Chat
              </button>

              <button
                className="ctXBtn ghost"
                onClick={() => {
                  const subject = "Schedule a Call - Course Enquiry";
                  const body = `Hi Crafting Brain,

I hope you're doing well. I'd like to schedule a quick call to enquire about your course details. Please let me know a convenient time for you this week.

Looking forward to connecting.

Best regards,
[Your Name]`;
                  window.location.href = `mailto:business@craftingbrain.com?subject=${encodeURIComponent(
                    subject
                  )}&body=${encodeURIComponent(body)}`;
                }}
              >
                📞 Schedule a Call
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactUs;